import React, { useMemo, useState } from "react";
import { Box, Stack, Typography, Grid, Dialog, DialogTitle, DialogContent } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import { PlatformWidget, HealthStatus } from "./types";
import {
  STATUS_META,
  withAlpha,
  deriveStatusKey,
  getTopSegments,
  pickPreviewAlerts,
} from "./PlatformHealthUtils";
import IconButton from '@mui/material/IconButton';
import { ReactComponent as ExpandIcon } from '../../assets/PepWise2.0/ExpandIcon.svg';

export type KPIItem = {
  priority_order: number;
  kpi_name: string;
  metric_name: string;
  severity: string;
  final_value: string;
  crossed_threshold: number;
  rank: number;
  unit: string;
  kpi_title?: string;
  resourceName?: string;
  issueSummary?: string;
  alertAge?: string;
  alertTime?: string;
};

type Props = {
  data: PlatformWidget;
  onOpenDrilldown: (platformId: string) => void;
  searchText?: string;
  isKPI?: boolean;
  kpiItems?: KPIItem[];
  allKpiItems?: KPIItem[];
};

const PlatformHealthWidget: React.FC<Props> = ({ data, onOpenDrilldown, searchText, isKPI = false, kpiItems = [], allKpiItems = [] }) => {
  const [kpiDialogOpen, setKpiDialogOpen] = useState(false);
  const theme = useTheme();
  const colors = theme.palette;
  const platformHealthColors = colors.KpiSeverityWidget;
  const query = (searchText || "").trim().toLowerCase();
  const isSearchActive = query.length > 0;

  const platformMatches = isSearchActive && (data.platformName || "").toLowerCase().includes(query);

  const getSeverityColor = (severity?: string): string => {
    const severityKey = (severity || "healthy").toLowerCase() as keyof typeof platformHealthColors.severity;
    return platformHealthColors.severity[severityKey] ?? platformHealthColors.severity.healthy ?? STATUS_META.HEALTHY.color;
  };

  const isRowMatch = (a: any) => {
    if (!isSearchActive) return false;
    return (
      (a.resourceName || "").toLowerCase().includes(query) ||
      (a.issueSummary || "").toLowerCase().includes(query)
    );
  };

  const hasAnyRowMatch = isSearchActive && (data.previewAlerts ?? []).some(isRowMatch);

  // Dynamically search through KPI items for realtime platforms
  const hasAnyKpiMatch = useMemo(() => {
    if (!isSearchActive || !isKPI) return false;
    const forbiddenFields = ['severity', 'status', 'crossed_threshold', 'priority_order', 'rank', 'unit', 'color'];
    const searchInItems = (items: any[]): boolean =>
      items.some((item: any) =>
        item && Object.entries(item).some(([key, val]) => 
          !forbiddenFields.includes(key) && 
          typeof val === "string" && 
          val.toLowerCase().includes(query)
        )
      );
    if (kpiItems.length > 0 && searchInItems(kpiItems)) return true;
    if (allKpiItems.length > 0 && searchInItems(allKpiItems)) return true;
    return false;
  }, [isSearchActive, isKPI, query, kpiItems, allKpiItems]);

  // Unified highlight flag: card turns blue if platform name OR content matches
  const isHighlighted = platformMatches || hasAnyRowMatch || hasAnyKpiMatch;

  const totalAlerts = data.alertCounts?.total ?? 0;
  const totalKPIAlerts = data.moreAlertsCount ?? 0;
  const showNoAlerts = totalAlerts === 0;

  const derivedStatus = useMemo(() => deriveStatusKey(data), [data]);
  const status = STATUS_META[derivedStatus] ?? STATUS_META.HEALTHY;

  const topSegments = useMemo(() => getTopSegments(data), [data]);

  const preview = useMemo(() => {
    const picked = pickPreviewAlerts(data, 2) ?? [];
    if (picked.length > 0) return picked;
    return (data.previewAlerts ?? []).slice(0, 2);
  }, [data]);

  const moreCount = isKPI ? totalKPIAlerts : Math.max(totalAlerts - preview.length, 0);

  const getDurationPillSx = (severity: HealthStatus) => {
    const severityColor = getSeverityColor(severity);

    return {
      px: 1,
      py: 0.35,
      borderRadius: "4px",
      fontWeight: 500,
      fontSize: "12px",
      color: severityColor,
      backgroundColor: withAlpha(severityColor, 0.18),
      border: `1px solid ${withAlpha(severityColor, 0.28)}`,
      whiteSpace: "nowrap" as const,
      alignSelf: "flex-start" as const,
      lineHeight: 1.2,
      minWidth: 44,
      textAlign: "center" as const,
      flexShrink: 0,
    };
  };

  const isClickable = !showNoAlerts && (!isSearchActive || isHighlighted);

  const renderContentSection = () => {
    if (showNoAlerts) {
      return (
        <Stack alignItems="center" justifyContent="center" flexGrow={1} spacing={1}>
          <AlarmOnIcon sx={{ color: platformHealthColors.severity.healthy, fontSize: "48px" }} />
          <Typography variant="widgetTitle" sx={{ color: platformHealthColors.severity.healthy }}>
            No active alerts
          </Typography>
        </Stack>
      );
    }

    if (isKPI && (kpiItems.length > 0 || allKpiItems.length > 0)) {
      const forbiddenFields = ['severity', 'status', 'crossed_threshold', 'priority_order', 'rank', 'unit', 'color'];
      
  
      const hasContentMatch = isSearchActive && !platformMatches && allKpiItems.some((kpi) =>
        kpi && Object.entries(kpi).some(([key, val]) => 
          !forbiddenFields.includes(key) && 
          typeof val === "string" && 
          val.toLowerCase().includes(query)
        )
      );

    
      const displayKpis = hasContentMatch
        ? allKpiItems.filter((kpi) =>
            kpi && Object.entries(kpi).some(([key, val]) => 
              !forbiddenFields.includes(key) && 
              typeof val === "string" && 
              val.toLowerCase().includes(query)
            )
          ).slice(0, 5)
        : kpiItems;

      return (
        <Grid container spacing={1} sx={{ width: "100%", m: 0 }}>
          {displayKpis.map((kpi, idx) => {
            const isLastOdd = displayKpis.length % 2 !== 0 && idx === displayKpis.length - 1;
            // Precise item match check: does THIS tile contain the search term?
            const kpiItemMatches = isSearchActive && !platformMatches && 
              kpi && Object.entries(kpi).some(([key, val]) => 
                !forbiddenFields.includes(key) && 
                typeof val === "string" && 
                val.toLowerCase().includes(query)
              );
            return (
              <Grid
                key={kpi.priority_order}
                item
                xs={isLastOdd ? 12 : 6}
              >
                <Box
                  sx={{
                    background: platformMatches
                      ? platformHealthColors.alertRow.platformHighlightBackground
                      : kpiItemMatches
                        ? platformHealthColors.kpi.highlightBackground
                        : platformHealthColors.kpi.background,
                    borderRadius: "8px",
                    p:1,
                    textAlign: "center",
                    pointerEvents: "none",
                    cursor: "default",
                    minHeight: 40,
                    height: 'calc(100% - 16px)',
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: "22px",
                      lineHeight: 1.2,
                      color: getSeverityColor(kpi.severity) || ((platformMatches || kpiItemMatches) ? platformHealthColors.text.highlight : platformHealthColors.text.primary),
                    }}
                  >
                    {kpi.final_value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "10px",
                      fontWeight: 500,
                      color: (platformMatches || kpiItemMatches) ? platformHealthColors.text.highlightMuted : platformHealthColors.text.muted,
                      mt: 0.3,
                      lineHeight: 1.2,
                      wordWrap: 'break-word',
                      width: '100%'
                    }}
                  >
                    {kpi.kpi_name}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      );
    }

    return preview.map((a) => {
      const rowMatches = isRowMatch(a);
      const defaultRowBg = rowMatches ? platformHealthColors.alertRow.highlightBackground : platformHealthColors.alertRow.background;
      const rowBg = platformMatches ? platformHealthColors.alertRow.platformHighlightBackground : defaultRowBg;
      const resourceColor = platformMatches || rowMatches ? platformHealthColors.text.highlight : platformHealthColors.text.primary;
      const summaryColor =
        platformMatches || rowMatches ? platformHealthColors.text.highlightSecondary : platformHealthColors.text.secondary;

      return (
        <Stack
          key={a.alertId}
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          sx={{
            background: rowBg,
            borderRadius: "6px",
            px: 1.2,
            py: 1,
            minWidth: 0,
            width: "100%",
            boxSizing: "border-box",
            pointerEvents: "none",
            cursor: "default",
          }}
        >
          <Box sx={{ pr: 1, flexGrow: 1, minWidth: 0, overflow: "hidden" }}>
            <Typography
              variant="bodyText"
              component="div"
              sx={{
                display: "block",
                fontWeight: 600,
                color: resourceColor,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={a.resourceName}
            >
              {a.resourceName}
            </Typography>
            <Typography
              variant="bodyText"
              component="div"
              sx={{
                display: "block",
                opacity: 0.85,
                fontSize: "12px",
                color: summaryColor,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={a.issueSummary}
            >
              {a.issueSummary}
            </Typography>
          </Box>

          {a.durationLabel ? (
            <Box sx={getDurationPillSx(a.severity)}>{a.durationLabel}</Box>
          ) : (
            <Box sx={{ ...getDurationPillSx(a.severity), opacity: 0.6 }}>--</Box>
          )}
        </Stack>
      );
    });
  };

  const renderFooterSection = () => {
    if (!(!showNoAlerts && (moreCount > 0 || isKPI))) {
      return <Box sx={{ height: 18 , mt: 1 }} />;
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {moreCount > 0 ? (
          <Typography
            variant="bodyText"
            sx={{
              fontSize: "12px",
              opacity: 0.9,
              color: platformMatches ? platformHealthColors.text.highlight : platformHealthColors.text.link,
              mt: 1,
            }}
            onClick={(e) => {
              e.stopPropagation();
              onOpenDrilldown(data.platformId);
            }}
          >
            {isKPI ? "Click here for alerts" : `+${moreCount} more alerts`}
          </Typography>
        ) : (
          <Box />
        )}
        { isKPI && <IconButton
          sx={{ height: '20', Width: '20px' }}
          onClick={(e) => {
            e.stopPropagation();
            setKpiDialogOpen(true);
          }}
        >
          <ExpandIcon style={{ rotate: '90deg' }} />
        </IconButton>}
      </Box>
    );
  };

  return (
    <Box
      onClick={() => {
        if (isClickable) onOpenDrilldown(data.platformId);
      }}
      sx={{
        cursor: isClickable ? "pointer" : "default",
        width: "100%",
        height: "100%",
        minHeight: 358,
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        display: "flex",
      }}
    >
      <Stack
        bgcolor={platformMatches ? platformHealthColors.card.highlightBackground : platformHealthColors.card.background}
        borderRadius="4px"
        spacing={0}
        sx={{
          width: "100%",
          height: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          padding: "24px 24px 8px 24px",
          gap: "16px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          border: `1px solid ${platformHealthColors.card.border}`,
          boxShadow: platformHealthColors.card.shadow,
          "&:hover": {
            boxShadow: isClickable
              ? platformHealthColors.card.hoverShadow
              : platformHealthColors.card.shadow,
          },
        }}
      >
        <Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ minWidth: 0, width: "100%" }}
          >
            <Typography
              variant="widgetTitle"
              color={platformMatches ? platformHealthColors.text.highlight : platformHealthColors.text.primary}
              sx={{
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                minWidth: 0,
                flex: 1,
                pr: 1,
              }}
            >
              {data.platformName}
            </Typography>

            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: "4px",
                fontWeight: 600,
                fontSize: "12px",
                color: getSeverityColor(derivedStatus),
                backgroundColor: withAlpha(getSeverityColor(derivedStatus), 0.12),
                border: `1px solid ${withAlpha(getSeverityColor(derivedStatus), 0.18)}`,
                flexShrink: 0,
                whiteSpace: "nowrap",
                pointerEvents: "none",
                cursor: "default",
              }}
            >
              {status.label}
            </Box>
          </Stack>

          {!showNoAlerts && !isKPI && topSegments.length > 0 && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                mt: 0.75,
                width: "100%",
                minWidth: 0,
                borderRadius: "6px",
                backgroundColor: platformMatches ? platformHealthColors.segment.highlightBackground : platformHealthColors.segment.background,
                p: "3px",
                boxSizing: "border-box",
                border: platformMatches
                  ? `1px solid ${platformHealthColors.segment.highlightBorder}`
                  : `1px solid ${platformHealthColors.segment.border}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  minWidth: 0,
                  gap: 0,
                  flexWrap: "nowrap",
                  overflow: "hidden",
                }}
              >
                {topSegments.map((seg, idx, arr) => (
                  <Box
                    key={seg.key}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      height: 28,
                      borderRadius:
                        idx === 0
                          ? "2px 0 0 2px"
                          : idx === arr.length - 1
                            ? "0 2px 2px 0"
                            : "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 600,
                      fontSize: "12px",
                      color: platformHealthColors.segment.text,
                      backgroundColor: getSeverityColor(seg.key),
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      cursor: "default",
                    }}
                  >
                    {seg.count} {seg.label}
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Stack>

        <Stack spacing={1} sx={{ flexGrow: 1, minHeight: 100, minWidth: 0 }}>
          {renderContentSection()}
        </Stack>

        {renderFooterSection()}
      </Stack>

      {/* ── KPI Expand Dialog ── */}
      {isKPI && (
        <Dialog
          open={kpiDialogOpen}
          onClick={(e) => { e.stopPropagation()}}
          onClose={() => setKpiDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "16px",
              p: 0,
              overflow: "hidden",
              minWidth: 400
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
              pt: 2.5,
              px: 3,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: "20px", color: platformHealthColors.dialog.title }}>
              {data.platformName}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                sx={{
                  fontSize: "13px",
                  fontWeight: 600,
                  color: getSeverityColor(derivedStatus),
                }}
              >
                {status.label}
              </Typography>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: getSeverityColor(derivedStatus),
                }}
              />
              <IconButton
                onClick={() => setKpiDialogOpen(false)}
                size="small"
                sx={{ ml: 0.5, color: platformHealthColors.dialog.closeIcon }}
              >
                ✕
              </IconButton>
            </Box>
          </DialogTitle>

          <DialogContent sx={{ px: 3, pb: 3, pt: '20px !important', background: platformHealthColors.dialog.background }}>
            <Grid container spacing={1.5}>
              {allKpiItems.map((item) => {
                const valueColor = getSeverityColor(item.severity);
                const displayValue = item.final_value ?? "--";

                return (
                  <Grid key={item.priority_order} item xs={6} sm={4} md={3}>
                    <Box
                      sx={{
                        background: platformHealthColors.kpi.background,
                        borderRadius: "10px",
                        p: 1.5,
                        textAlign: "center",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 70,
                        border: item.crossed_threshold ? `1.5px solid ${withAlpha(valueColor, 0.19)}` : "1.5px solid transparent",
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: "22px",
                          lineHeight: 1.2,
                          color: item.final_value ? valueColor : platformHealthColors.text.disabled,
                        }}
                      >
                        {displayValue}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 500,
                          color: platformHealthColors.text.muted,
                          mt: 0.5,
                          lineHeight: 1.2,
                          wordWrap: "break-word",
                          width: "100%",
                        }}
                      >
                        {item.kpi_name}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </DialogContent>
        </Dialog>
      )}
    </Box>
  );
};

export default PlatformHealthWidget;